/** @jsx jsx */
import React from 'react';
import { jsx } from '@emotion/core';
import { Box, Checkbox, FormControlLabel } from '@material-ui/core';
import { Field, FieldProps, Form, FormikProps } from 'formik';

import styles from '../styles';

import { Trans, useMarkdownTranslate } from 'hooks/useTranslate/useTranslate';
import { ShippingAddressType, Order } from 'state/creditApplication';

import PageContainer from 'components/PageContainer';
import Title from 'components/Title';
import ActionButtonContainer from 'components/ActionButtonsContainer';
import Button from 'components/Button';

import OrderSummary from '../OrderSummary';
import ShippingAddress from '../ShippingAddress';
import ShippingMethod from '../ShippingMethod';
import PaymentPlan from '../PaymentPlan';
import { HostedFields } from 'braintree-web';
import BrainTree from './BrainTree';
import DisclosureLink from 'features/unified/DisclosureLink/DisclosureLink';
import LoadingPage from 'components/LoadingPage';
import BackToPrevious from 'components/unified/BackToPrevious';
import { SplitPayOfferData } from 'state/financeOffers';
import { decimalMoneyMask, conformToMask } from 'utils/masks';

export interface FormValues {
  consent: boolean;
}

type PropTypes = FormikProps<FormValues> & {
  order: Order | null;
  shippingAddress: ShippingAddressType | null;
  splitPayOfferData: SplitPayOfferData;
  hostedFieldsValidity: boolean;
  hostedFieldsInstance: HostedFields | undefined;
  biweeklyPayment: number;
  setHostedFieldsValidity: React.Dispatch<React.SetStateAction<boolean>>;
  setHostedFieldsInstance: React.Dispatch<
    React.SetStateAction<HostedFields | undefined>
  >;
  cachedLoanAgreementDisclosureContent: string;
  paymentAgreementId: string;
  hasDisclosureError: boolean;
  handleDisclosureError: () => void;
  getDisclosureLoadingState: (type?: string) => boolean;
  setDisclosureLoadingState: (type: string, loading: boolean) => void;
};

const SplitPayPlaceOrderView: React.FC<PropTypes> = props => {
  const {
    isValid,
    isSubmitting,
    order,
    shippingAddress,
    splitPayOfferData,
    hostedFieldsValidity,
    hostedFieldsInstance,
    biweeklyPayment,
    cachedLoanAgreementDisclosureContent,
    setHostedFieldsValidity,
    setHostedFieldsInstance,
    paymentAgreementId,
    hasDisclosureError,
    handleDisclosureError,
    getDisclosureLoadingState,
    setDisclosureLoadingState,
  } = props;

  const tMd = useMarkdownTranslate();

  if (getDisclosureLoadingState('loan-agreement-sp')) {
    return <LoadingPage headerTitle="Just a moment." />;
  }

  return (
    <Form noValidate>
      <PageContainer maxWidth="sm" data-testid="test">
        <Box mb={2}>
          <Title css={styles.pageTitleText}>
            {tMd('unified.placeOrder.splitPay.title')}
          </Title>
        </Box>

        <Box mb={3} css={styles.subtitleText}>
          {tMd('unified.placeOrder.splitPay.subtitle', {
            amount: conformToMask(
              splitPayOfferData.principal.toFixed(2),
              decimalMoneyMask,
            ),
          })}
        </Box>

        {order ? (
          <Box mb={2} css={styles.bodyContainer}>
            <OrderSummary order={order} />
          </Box>
        ) : null}

        {shippingAddress ? (
          <Box mb={2} css={styles.bodyContainer}>
            <ShippingAddress shippingAddress={shippingAddress} />
          </Box>
        ) : null}

        {order ? (
          <Box mb={2} css={styles.bodyContainer}>
            <ShippingMethod order={order} />
          </Box>
        ) : null}

        <Box mb={2} css={styles.bodyContainer}>
          <PaymentPlan splitPayOfferData={splitPayOfferData} />
        </Box>

        <BrainTree
          biweeklyPayment={biweeklyPayment}
          setHostedFieldsValidity={setHostedFieldsValidity}
          setHostedFieldsInstance={setHostedFieldsInstance}
        />

        <Box mb={3} css={styles.consentField}>
          <Field name="consent" type="checkbox">
            {(fieldProps: FieldProps) => (
              <FormControlLabel
                label={
                  <div css={styles.checkboxLabel}>
                    <Trans
                      i18nKey="unified.placeOrder.splitPay.consent"
                      components={[
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          type="credit-score-notice"
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          content={cachedLoanAgreementDisclosureContent}
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                        <DisclosureLink
                          paymentAgreementId={paymentAgreementId}
                          type="payment-full-terms"
                          onError={handleDisclosureError}
                          setLoadingState={setDisclosureLoadingState}
                        />,
                      ]}
                    />
                  </div>
                }
                control={
                  <Checkbox
                    id="accountAssureConsent"
                    aria-label="accountAssureConsentLabel"
                    {...fieldProps.field}
                  />
                }
              />
            )}
          </Field>
        </Box>

        <ActionButtonContainer>
          <Button
            mobileFullWidth={true}
            id="formSubmit"
            aria-label="formSubmitButton"
            type="submit"
            disabled={
              !isValid ||
              !hostedFieldsValidity ||
              !hostedFieldsInstance ||
              isSubmitting ||
              hasDisclosureError ||
              getDisclosureLoadingState()
            }
            loading={isSubmitting}
          >
            {tMd('unified.placeOrder.label.submitButton')}
          </Button>

          <BackToPrevious tMdKey="unified.placeOrder.label.backButton" />
        </ActionButtonContainer>
      </PageContainer>
    </Form>
  );
};

export default SplitPayPlaceOrderView;
